import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Our LocalBest Trusted Network™" — public section that renders on a
 * business profile page when its owner has admin-approved partner businesses.
 * Renders nothing if there are no partners.
 */
export default function TrustedNetworkSection({ slug }) {
  const [partners, setPartners] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    axios.get(`${API}/businesses/${slug}/trusted-network`)
      .then((r) => { if (!cancelled) { setPartners(r.data?.partners || []); setLoaded(true); } })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [slug]);

  if (!loaded) return null;
  if (partners.length === 0) return null;

  const trackClick = (partnerSlug) => {
    // Fire-and-forget — don't block navigation
    axios.post(`${API}/businesses/${slug}/trusted-network/track-click`, null, {
      params: { partner_slug: partnerSlug },
    }).catch(() => {});
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="glass-card p-6 md:p-8 rounded-2xl border border-[#7C3AED]/20 relative overflow-hidden"
      data-testid="trusted-network-section"
    >
      {/* Background accents */}
      <div className="pointer-events-none absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#7C3AED]/10 blur-[100px]" />
      <div className="pointer-events-none absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-[#EC4899]/10 blur-[100px]" />

      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#7C3AED]/20 to-[#EC4899]/20 border border-[#7C3AED]/40">
            <Sparkles className="w-3.5 h-3.5 text-[#A78BFA]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#A78BFA] font-bold">Trusted Network</span>
          </div>
        </div>
        <h2 className="text-xl md:text-2xl font-bold font-['Outfit'] text-white mb-2">
          Our <span className="bg-gradient-to-r from-[#7C3AED] via-[#A78BFA] to-[#EC4899] bg-clip-text text-transparent">LocalBest Trusted Network</span><sup className="text-xs">™</sup>
        </h2>
        <p className="text-sm text-[#94A3B8] mb-6 max-w-2xl">
          Looking for other great local professionals? Explore businesses we know, like, and trust throughout the LocalBest.ai community.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {partners.map((p) => (
            <Link
              key={p.slug}
              to={`/business/${p.slug}`}
              onClick={() => trackClick(p.slug)}
              className="group relative rounded-xl bg-[#0F172A] hover:bg-[#1E293B] border border-white/10 hover:border-[#7C3AED]/40 transition-all p-4 flex flex-col"
              data-testid={`trusted-partner-${p.slug}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {p.logo_url ? (
                    <img src={p.logo_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <span className="text-white font-bold text-lg">{(p.name || "?")[0]}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-white font-semibold text-sm leading-tight group-hover:text-[#A78BFA] transition-colors truncate">{p.name}</h3>
                  {p.category_name && (
                    <p className="text-[11px] text-[#7C3AED] font-medium uppercase tracking-wider mt-0.5 truncate">{p.category_name}</p>
                  )}
                  {(p.city || p.state) && (
                    <p className="text-[11px] text-[#64748B] mt-0.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {[p.city, p.state].filter(Boolean).join(", ")}
                    </p>
                  )}
                </div>
              </div>

              {p.endorsement_text ? (
                <blockquote className="text-xs text-[#E2E8F0] italic border-l-2 border-[#7C3AED]/50 pl-2.5 my-2">
                  "{p.endorsement_text}"
                </blockquote>
              ) : p.short_description ? (
                <p className="text-xs text-[#94A3B8] line-clamp-2 my-2">{p.short_description}</p>
              ) : null}

              <div className="mt-auto pt-2 flex items-center justify-between">
                <span className="text-[10px] text-[#64748B] uppercase tracking-wider font-semibold">Recommended Partner</span>
                <span className="inline-flex items-center gap-1 text-[11px] text-[#A78BFA] font-semibold group-hover:gap-2 transition-all">
                  View Listing <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </motion.section>
  );
}
